"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VariantPicker = void 0;
const base_component_1 = require("../../components/base-component");
const dom_1 = require("../../utils/dom");
const utils_1 = require("../../utils/utils");
const key_1 = require("../../utils/key");
const ELEMENTS = [
    'sticky-product-buy-btn',
    'product-buy-btn',
    'product-quantity',
];
const BLOCKS = [
    'product-price',
    'product-badges',
    'product-sku',
    'product-stock',
];
const VARIANTS_JSON_SELECTOR = '[data-variant-picker-variants]';
const OPTION_INPUTS_SELECTOR = '[data-variant-picker-option-inputs]';
const OPTION_LABELS_SELECTOR = '[data-variant-picker-option-labels]';
const DROPDOWN_LABEL_SELECTOR = '[data-dropdown-variant-picker-label]';
const ARROW_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
const HEADER_SELECTOR = 'header-component';
class VariantPicker extends base_component_1.BaseComponent {
    variants;
    variant;
    constructor() {
        super();
        this.variants = JSON.parse((0, utils_1.$el)(VARIANTS_JSON_SELECTOR, this).textContent);
    }
    mountComponent() {
        this.setVariant();
        this.setOptionsAvailable();
        if (this.hasAttribute('data-initial-selected-variant')) {
            this.setFirstMedia(true);
        }
        this.updateBlocks();
        this.setListeners();
    }
    handleVariantChange = event => {
        this.setVariant();
        const targetUrl = event.target.dataset.productUrl;
        if (targetUrl && this.dataset.url !== targetUrl) {
            this.swapProduct(targetUrl);
            return;
        }
        this.setOptionsAvailable();
        this.updateLabels();
        this.updateStock();
        if (!this.variant) {
            return;
        }
        this.updatePickupAvailability();
        this.updateURL();
        this.updateVariantInput();
        this.updateSection();
        if (!this.hasAttribute('data-product-media-filtering-option')) {
            this.setFirstMedia(false);
        }
        this.emit('change', {
            variant: this.variant,
            option: {
                name: event.target.name,
                value: event.target.value,
            },
        });
    };
    toggleHeaderDynamicShow = bool => {
        const header = (0, utils_1.$el)(HEADER_SELECTOR);
        header.toggleLockDynamicShow(bool);
    };
    swapProduct(productUrl) {
        const product = this.closest('.shopify-section');
        const quickView = this.closest('quick-view');
        const options = (0, utils_1.$list)(OPTION_INPUTS_SELECTOR, this);
        const selectedOptionsIds = options.map(option => this.getCheckedInput((0, utils_1.$list)('input', option)).dataset.valueId);
        const params = this.variant
            ? `variant=${this.variant?.id}`
            : `option_values=${selectedOptionsIds.join(',')}`;
        fetch(`${productUrl}?section_id=${this.dataset.sectionIdWithoutPrefix}&${params}`)
            .then(response => response.text())
            .then(responseText => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            if (product) {
                const newProduct = html.getElementById(`shopify-section-${this.dataset.sectionIdWithoutPrefix}`);
                product.innerHTML = newProduct.innerHTML;
            }
            else if (quickView) {
                quickView.setProductFromHTML(html);
            }
        });
        window.history.replaceState({}, '', `${productUrl}`);
    }
    updateLabels() {
        const inputContainers = (0, utils_1.$list)(OPTION_INPUTS_SELECTOR, this);
        const labelContainers = (0, utils_1.$list)(OPTION_LABELS_SELECTOR, this);
        inputContainers.forEach((inputContainer, idx) => {
            const labels = (0, utils_1.$list)('label', labelContainers[idx]);
            const inputs = (0, utils_1.$list)('input', inputContainer);
            const input = this.getCheckedInput(inputs);
            const dropdownLabel = (0, utils_1.$el)(DROPDOWN_LABEL_SELECTOR, labelContainers[idx]);
            if (dropdownLabel) {
                dropdownLabel.toggleAttribute('no-available', !this.variant?.available);
            }
            labels.forEach(label => {
                const isSelected = label.getAttribute('for') === input.id;
                if (dropdownLabel && isSelected) {
                    const dropdown = (0, utils_1.$elParent)('dropdown-opener', dropdownLabel);
                    (0, dom_1.replaceNodeChildren)(dropdownLabel, label);
                    dropdown.trigger();
                }
                label.classList.toggle('selected', isSelected);
            });
        });
    }
    updateURL() {
        if (!this.variant || this.dataset.updateUrl === 'false') {
            return;
        }
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }
    updateStock() {
        const stock = (0, utils_1.$el)(`#product-stock-inner-${this.dataset.productId}-${this.dataset.section}`);
        stock?.classList.toggle('hidden', !this.variant);
    }
    setVariant() {
        const options = (0, utils_1.$list)(OPTION_INPUTS_SELECTOR, this);
        const selectedOptions = options.map(option => this.getCheckedInput((0, utils_1.$list)('input', option)).value);
        this.variant = this.variants.find(({ options }) => options.every((option, index) => selectedOptions[index] === option));
        if (!this.variant) {
            (0, utils_1.whenDefined)('product-form').then(() => {
                this.setUnavailable();
            });
        }
    }
    setUnavailable() {
        this.getForm()?.setDisable(true, null);
        this.getForm()?.setButtonText(window.lbcThemeLocales.variantStrings.unavailable);
    }
    setListeners() {
        const dropdowns = (0, utils_1.$list)('float-element', this);
        const dropdownLabels = dropdowns
            .map(dropdown => (0, utils_1.$list)('label', dropdown.element))
            .flat(1);
        const simpleLabels = (0, utils_1.$list)('label', this);
        const labels = [...dropdownLabels, ...simpleLabels];
        this.addListener(this, 'keydown', this.handleKeydown);
        labels.forEach(label => {
            this.addListener(label, 'keydown', this.handleLabelKeydown);
        });
        this.addListener(this, 'change', this.handleVariantChange);
    }
    handleLabelKeydown = (event) => {
        if ((0, key_1.isEnterKey)(event)) {
            event.preventDefault();
            const target = event.target;
            const input = (0, utils_1.$el)(`input[id="${target.getAttribute('for')}"]`, this);
            if (input) {
                input.dispatchEvent(new MouseEvent('click', { button: 0, which: 1 }));
            }
        }
    };
    handleKeydown = (event) => {
        const isArrowKey = ARROW_KEYS.includes(event.key);
        if (!isArrowKey && !(0, key_1.isTabKey)(event)) {
            return;
        }
        const target = event.target;
        const labelContainer = (0, utils_1.$elParent)(OPTION_LABELS_SELECTOR, target);
        if (!labelContainer) {
            this.removeListener(document, 'keydown', this.handleLabelKeydown);
            return;
        }
        const labels = (0, utils_1.$list)('label', labelContainer);
        const index = labels.findIndex(label => label === event.target);
        const currentLabel = labels[index];
        const firstLabel = labels[0];
        const lastLabel = labels[labels.length - 1];
        const nextLabel = labels[index + 1];
        const prevLabel = labels[index - 1];
        const setActiveLabel = newLabel => {
            currentLabel?.removeAttribute('tabindex');
            newLabel.setAttribute('tabindex', 0);
            newLabel.focus();
            event.preventDefault();
        };
        const changeActiveLabelOnNext = () => {
            setActiveLabel(nextLabel || firstLabel);
        };
        const changeActiveLabelOnPrev = () => {
            setActiveLabel(prevLabel || lastLabel);
        };
        const callback = {
            ArrowLeft: changeActiveLabelOnPrev,
            ArrowRight: changeActiveLabelOnNext,
            ArrowUp: changeActiveLabelOnPrev,
            ArrowDown: changeActiveLabelOnNext,
        }[event.key];
        if ((0, key_1.isTabKey)(event)) {
            if (prevLabel && event.shiftKey) {
                changeActiveLabelOnPrev();
            }
            else if (nextLabel && !event.shiftKey) {
                changeActiveLabelOnNext();
            }
        }
        else {
            callback();
        }
    };
    getVariantsByOption(prevOptions) {
        const availableVariants = this.variants
            .filter(variant => variant.available)
            .map(variant => variant.options);
        if (!prevOptions) {
            return availableVariants;
        }
        return availableVariants.filter(options => prevOptions.every(prevOption => options.includes(prevOption.value)));
    }
    async getDropdownElementBySelector(selector) {
        const dropdowns = (0, utils_1.$list)('float-element', this);
        let elementBySelector;
        await (0, utils_1.whenDefined)('float-element').then(() => {
            elementBySelector = (0, utils_1.$el)(selector, dropdowns.find(dropdown => (0, utils_1.$el)(selector, dropdown.element)).element);
        });
        return elementBySelector;
    }
    async setOptions(options, possibleVariants) {
        for (const option of options) {
            const available = possibleVariants.some(variant => variant.includes(option.value));
            const labelSelector = `label[for="${option.id}"]`;
            const label = (0, utils_1.$el)(labelSelector, this) ||
                (await this.getDropdownElementBySelector(labelSelector));
            label.toggleAttribute('no-available', !available);
        }
    }
    setOptionsAvailable() {
        const options = (0, utils_1.$list)(OPTION_INPUTS_SELECTOR, this);
        const firstOptions = options[0] && (0, utils_1.$list)('input', options[0]);
        const firstOption = this.getCheckedInput(firstOptions);
        this.setOptions(firstOptions, this.getVariantsByOption([]));
        const secondOptions = options[1] && (0, utils_1.$list)('input', options[1]);
        if (secondOptions) {
            const secondOption = this.getCheckedInput(secondOptions);
            this.setOptions(secondOptions, this.getVariantsByOption([firstOption]));
            const thirdOptions = options[2] && (0, utils_1.$list)('input', options[2]);
            if (thirdOptions) {
                this.setOptions(thirdOptions, this.getVariantsByOption([firstOption, secondOption]));
            }
        }
    }
    setFirstMedia(isForceAnimation) {
        const mediaList = (0, utils_1.$list)(`#product-media-${this.dataset.productId}-${this.dataset.section}`);
        mediaList.forEach(media => media.updateFirstMediaByVariant(this.variant, isForceAnimation));
    }
    setFirstMediaByVariant(variantId) {
        const mediaList = (0, utils_1.$list)(`#product-media-${this.dataset.productId}-${this.dataset.section}`);
        mediaList.forEach(media => media.updateFirstMediaByVariant(variantId));
    }
    updateVariantInput() {
        const form = (0, utils_1.$el)(`#product-form-component-${this.dataset.productId}-${this.dataset.section}`);
        if (!form) {
            return;
        }
        form.getVeriantInput().setAttribute('value', String(this.variant.id));
        form.getVeriantInput().dispatchEvent(new Event('change', { bubbles: true }));
    }
    updatePickupAvailability() {
        const pickupAvailability = (0, utils_1.$el)(`#product-pickup-availability-${this.dataset.productId}-${this.dataset.section}`);
        if (pickupAvailability) {
            pickupAvailability.updateVariantId(this.variant.id);
            pickupAvailability.fetchAvailability();
        }
    }
    updateSection() {
        this.toggleHeaderDynamicShow(true);
        const form = (0, utils_1.$el)(`#product-form-component-${this.dataset.productId}-${this.dataset.section}`);
        this.updateBlocks();
        form?.setDisable(true, null);
        form?.setLoading(true, null);
        fetch(`${this.dataset.url}?variant=${this.variant.id}&section_id=${this.dataset.sectionIdWithoutPrefix}`)
            .then(response => response.text())
            .then(responseText => {
            form?.setDisable(!this.variant.available, null);
            form?.setLoading(false, null);
            this.updateElements((0, dom_1.parseHTML)(responseText));
        })
            .catch(() => {
            console.log('Error in variant picker component');
        })
            .finally(() => {
            this.toggleHeaderDynamicShow(false);
        });
    }
    updateElements(html) {
        ELEMENTS.forEach(block => {
            const id = `${block}-${this.dataset.productId}-${this.dataset.section}`;
            const target = (0, utils_1.$el)(`#${id}`);
            const newTarget = (0, utils_1.$el)(`#${id}`, html);
            (0, dom_1.replaceNodeChildren)(target, newTarget);
        });
    }
    updateBlocks() {
        BLOCKS.forEach(blockId => {
            const block = (0, utils_1.$el)(`#${blockId}-${this.dataset.productId}-${this.dataset.section}`);
            block?.updateByVariantId(this.variant.id);
        });
    }
    getCheckedInput(inputs) {
        return inputs.find(({ checked }) => checked);
    }
    getForm() {
        return (0, utils_1.$el)(`#product-form-component-${this.dataset.productId}-${this.dataset.section}`);
    }
}
exports.VariantPicker = VariantPicker;
